import { beginManagedImport } from '../services/runtimeRecovery';

/**
 * Retry a dynamic import() with backoff before giving up.
 *
 * Why: right after a Cloudflare Pages deploy there's a short edge-propagation
 * window where a freshly-referenced hashed chunk can 404 on some nodes. A
 * blind failure crashes React.lazy into the ErrorBoundary; two quick retries
 * ride out the window invisibly. If retries still fail, the error propagates
 * and the runtime-recovery reload takes over (stale-shell case).
 *
 * Also rejects when the module object is missing / has no usable default —
 * React.lazy then throws `undefined is not an object (evaluating '_result.default')`
 * which surfaces as the full-app "Something went wrong" screen.
 */
export async function retryImport<T>(
  factory: () => Promise<T>,
  // Six retries with growing backoff — roughly 1.2s, 1.9, 3.1, 4.9, 7.9, 12.6,
  // about 32 seconds of patience.
  //
  // Three retries (~14s) was not enough. A Pages deploy leaves the open tab
  // holding the previous index.html, and its chunk hashes answer 503 "still
  // propagating" until the edge catches up — measured at five to ten seconds,
  // sometimes longer. Fourteen seconds had no margin over that, so an ordinary
  // deploy could push a lazy route into the error screen.
  //
  // React.lazy caches a rejection FOREVER, so losing this race is not a blip:
  // that component is dead for the life of the page even after the chunk goes
  // 200. Waiting beats crashing — the user sees a spinner, not a red screen.
  retries = 6,
  delayMs = 1200,
): Promise<T> {
  const finish = beginManagedImport();
  try {
    for (let attempt = 0; ; attempt++) {
      try {
        const mod = await factory();
        if (mod == null) throw new Error('Dynamic import resolved to null/undefined module');
        if (typeof mod === 'object' && (mod as { default?: unknown }).default == null) {
          throw new Error('Dynamic import resolved to empty module (no default export)');
        }
        return mod;
      } catch (error) {
        if (attempt >= retries) throw error;
        await new Promise<void>(resolve => window.setTimeout(resolve, delayMs));
        delayMs = Math.round(delayMs * 1.6);
      }
    }
  } finally {
    finish();
  }
}

/** Safe lazy factory: prefer named export, fall back to default. */
export function lazyNamed<T extends Record<string, unknown>>(
  factory: () => Promise<T>,
  exportName: keyof T & string,
): Promise<{ default: T[keyof T] }> {
  return retryImport(factory).then((m) => {
    const named = m?.[exportName];
    const fallback = (m as { default?: T[keyof T] })?.default;
    const component = (named ?? fallback) as T[keyof T] | undefined;
    if (component == null) {
      throw new Error(`Lazy module missing export "${exportName}" and default`);
    }
    return { default: component };
  });
}
